import React, {
  useCallback,
  useEffect,
  useMemo, 
  useState,
} from "react";

import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { Navigation } from "./../../../utils/navigation";
import { ResetListIcon, InfoIcon } from "../../../assets";
import classNames from "classnames";

import {
  fetchRides,
  setMyFilter,
  saveCustomUserFilter,
  applyMyFilter,
  setFilteredStatus,
  generateReport,
  setShowRefreshNotification,
  resetRides,
  fetchUberNeedAttentionCount,
  acknowledgeUberNeedAttention,
} from "../../../store/rides";

import { clearTCInfo } from "../../../store/transport_organizations";
import { clearHOInfo } from "../../../store/health_organizations";
import { useCurrentRole } from "../../../hooks/useCurrentRole";
import {
  Pagination,
  Button,
  Tooltip,
  useExportRidesModal,
  BaseTable,
} from "../../../components";
import { LodashHelper } from "../../../utils";
import { destroyNotification } from "../../../components/common/ConfirmPopUp/ConfirmPopUp";
import { URLS } from "../../../constants/constants";
import { usePagination } from "../../../hooks/usePagination";
import { redirectToPage } from "../../../utils/helpers";
import { checkForOwnFilters } from "../../../store/selectors/common";
import styles from "./index.module.css";
 

const transformData = (data) =>
  data?.length > 0 ? data?.map((d) => ({ ...d, key: d.seqId })) : [];

const Rides = ({
  fetchRides,
  rides,
  limit,
  currentPage,
  total,
  totalPages,
  role,
  columns,
  renderTableHead,
  dataLoaded,
  hasOwnFilter,
  setRequestsOnly,
  setNewRequestOnly,
  setMyFilter,
  savedFilterParams,
  myFilterParams,
  myFilterApplied,
  saveCustomUserFilter,
  applyMyFilter,
  setFilteredStatus,
  generateReport,
  hasSavedFilterParams,
  setShowRefreshNotification,
  clearTCInfo,
  clearHOInfo,
  resetRides,
  fetchUberNeedAttentionCount,
  acknowledgeUberNeedAttention,
  uberNeedAttentionCount,
  uberNeedAttentionRideIds,
  showNeedAttentionBtn,
  userId,
  setNeedAttentionFilter,
  loading,
  horizontalScroll,
}) => {
  const navigation = Navigation();
  const { location } = navigation;

  usePagination(navigation);
  const { isMHAdmin, isTCWorker } = useCurrentRole(role);

  const [myFilterTouched, setMyFilterTouched] = useState(false);
  const [showNoTripsNeedAttention, setShowNoTripsNeedAttention] = useState(false);

  const handleConfirmExport = useCallback(
    (payload, cb) => generateReport(payload, cb),
    [generateReport]
  );

  const { Modal, setExportModalVisible } = useExportRidesModal({
    onConfirm: handleConfirmExport,
  });

  const toggleExportModal = useCallback(
    (flag) => () => setExportModalVisible(flag),
    [setExportModalVisible]
  );

  const clearFilters = useCallback(() => {
    setMyFilter({});
    applyMyFilter(false);
    setShowNoTripsNeedAttention(false);
    if (showNeedAttentionBtn) {
      acknowledgeUberNeedAttention();
    }
  }, [applyMyFilter, setMyFilter, showNeedAttentionBtn, acknowledgeUberNeedAttention]);

  const handleNeedAttentionClick = useCallback(() => {
    const pendingRideIds = (uberNeedAttentionRideIds || []).filter(Boolean);
    if (pendingRideIds.length) {
      setShowNoTripsNeedAttention(false);
      setNeedAttentionFilter?.();
    } else {
      setMyFilter({});
      applyMyFilter(false);
      setShowNoTripsNeedAttention(true);
    }
    if (showNeedAttentionBtn) {
      acknowledgeUberNeedAttention();
    }
  }, [
    setNeedAttentionFilter,
    setMyFilter,
    applyMyFilter,
    showNeedAttentionBtn,
    acknowledgeUberNeedAttention,
    uberNeedAttentionRideIds,
  ]);

  useEffect(() => {
    location?.state?.rider &&
      setMyFilter({
        formattedRiderName: `${location?.state?.rider?.firstName} ${location?.state?.rider?.lastName}`,
        rider: location?.state?.rider?.id,
      });

    return () => {};
  }, [location, setMyFilter]);

  useEffect(() => { 
    fetchRides(false, navigation);

    return () => {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRides]);

  useEffect(() => {
    if (hasOwnFilter) setMyFilterTouched(true);

    return () => {};
  }, [hasOwnFilter]);

  useEffect(
    () =>   {
      if (!location.pathname.includes(URLS.RIDES)) {
        setMyFilter({});
      }
    },
    [setMyFilter, location?.pathname]
  );

  useEffect(
    () => () => {
      applyMyFilter(false);
      setFilteredStatus(false);
      setShowRefreshNotification(false);
      destroyNotification();
      clearTCInfo();
      clearHOInfo();
      resetRides();
    },
    [
      applyMyFilter,
      setFilteredStatus,
      setMyFilter,
      setShowRefreshNotification,
      clearTCInfo,
      clearHOInfo,
      resetRides,
    ]
  );

  useEffect(() => {
    if (!showNeedAttentionBtn || !userId || !dataLoaded || loading) return;
    const timer = setTimeout(() => {
      fetchUberNeedAttentionCount();
    }, 400);
    return () => clearTimeout(timer);
  }, [
    showNeedAttentionBtn,
    userId,
    dataLoaded,
    loading,
    fetchUberNeedAttentionCount,
    myFilterParams,
  ]);

  useEffect(
    () =>  {
      if (
        savedFilterParams &&
        hasOwnFilter &&
        !LodashHelper.isEqual(myFilterParams, savedFilterParams)
      ) {
        applyMyFilter(hasOwnFilter);
      }

      return;
    },
    [hasOwnFilter, applyMyFilter, myFilterParams, savedFilterParams]
  );

  const handleChangePage = useCallback(
    async(page) => {

    
     // if (currentPage !== page) {
        redirectToPage(URLS.RIDES, page, navigation);
        fetchRides(false, navigation);
     // }
    },
    [fetchRides, navigation]
  );
  const handleSaveMyFilter = useCallback(() => {
    saveCustomUserFilter(myFilterParams);
    setFilteredStatus(true);
  }, [myFilterParams, setFilteredStatus, saveCustomUserFilter]);

  const handleApplyMyFilter = useCallback(() => {
    setMyFilter(savedFilterParams);
    applyMyFilter(true);
    setFilteredStatus(true);
  }, [applyMyFilter, savedFilterParams, setFilteredStatus, setMyFilter]);

  const handleRowClick = useCallback(
    (record) => () => navigation?.push(`/rides/${record.id}`),
    [navigation]
  );

  const showTCFilterBtn = isTCWorker && !myFilterApplied;

  const showMHFilterBtn = isMHAdmin && !myFilterApplied;

  const uberNeedAttentionBadgeDisplay =
    typeof uberNeedAttentionCount === "number"
      ? Math.max(0, uberNeedAttentionCount)
      : 0;
  const showNeedAttentionBlink =
    showNeedAttentionBtn &&
    typeof uberNeedAttentionCount === "number" &&
    uberNeedAttentionBadgeDisplay > 0;

  const showApplyFilterBtn =
    hasSavedFilterParams &&
    !myFilterApplied &&
    !LodashHelper.isEqual(savedFilterParams, myFilterParams);

  const showUpdateFilterBtn =
    hasSavedFilterParams &&
    myFilterTouched &&
    !LodashHelper.isEqual(savedFilterParams, myFilterParams);

  const components = useMemo(
    () => ({
      header: {
        wrapper: dataLoaded && renderTableHead,
      },
    }),
    [dataLoaded, renderTableHead]
  );

  const ridesTable = useMemo(
    () => (
      <BaseTable
        data={transformData(rides)}
        components={components}
        renderTableHead={renderTableHead}
        columns={dataLoaded && columns}
        onRowClick={handleRowClick}
        isTCWorker={isTCWorker}
        addHoverEffectToRowTable
        horizontalScroll={horizontalScroll}
      />
    ),
    [
      rides,
      dataLoaded,
      columns,
      renderTableHead,
      handleRowClick,
      components,
      isTCWorker,
      horizontalScroll,
    ]
  );

  return (
    <>
      <div className={styles.pageContent}>
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>Rides</h1>

          <div className={styles.filterBtns}>
            {showTCFilterBtn && (
              <div className={styles.filterBtn}>
                <Button
                  myClassName={styles.navigationBtn}
                  onClick={setNewRequestOnly}
                >
                  View New Ride Requests
                </Button>
              </div>
            )}
            {showNeedAttentionBtn && (
              <div className={styles.filterBtn}>
                <Button
                  myClassName={classNames(styles.navigationBtn, styles.needAttentionBtn)}
                  onClick={handleNeedAttentionClick}
                >
                  <span className={styles.needAttentionLabel}>Needs Attention</span>
                  {showNeedAttentionBlink && (
                    <>
                      <span className={styles.needAttentionCount}>
                        {uberNeedAttentionBadgeDisplay}
                      </span>
                    </>
                  )}
                </Button>
              </div>
            )}
            {showMHFilterBtn && (
              <div className={styles.filterBtn}>
                <Button
                  myClassName={styles.navigationBtn}
                  onClick={setRequestsOnly}
                >
                  View In-Progress Rides
                </Button>
              </div>
            )}

            {showApplyFilterBtn ? (
              <div className={styles.filterBtn}>
                <Button
                  myClassName={styles.navigationBtn}
                  onClick={handleApplyMyFilter}
                >
                  Apply Saved Filter
                </Button>
              </div>
            ) : (
              showUpdateFilterBtn && (
                <div className={styles.filterBtn}>
                  <Button
                    myClassName={styles.navigationBtn}
                    onClick={handleSaveMyFilter}
                  >
                    Update Filter Settings
                  </Button>
                </div>
              )
            )}

            {!hasSavedFilterParams && (
              <div className={styles.filterBtn}>
                <Button
                  myClassName={styles.navigationBtn}
                  disabled={!hasOwnFilter}
                  onClick={handleSaveMyFilter}
                >
                  Save my Filter
                </Button>
              </div>
            )}

            {(hasOwnFilter || showNoTripsNeedAttention) && (
              <div className={styles.filterBtn}>
                <Button
                  myClassName={styles.navigationBtn}
                  onClick={clearFilters}
                >
                  <ResetListIcon width={16} height={16} className={styles.resetListIcon} /> Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>

        {showNoTripsNeedAttention ? (
          <div className={styles.noTripsNeedAttentionBanner}>
            No trips need attention today
          </div>
        ) : (
          <div>{ridesTable}</div>
        )}
      </div>
      {!showNoTripsNeedAttention && (
      <div className={styles.tableFooter}>
        <div className={styles.export}>
          <Button
            myClassName={styles.exportBtn}
            disabled={!rides?.length}
            onClick={toggleExportModal(true)}
          >
            Export Report
          </Button>
          <Tooltip
            title={
              <span>
                To export all data, clear all filters. If filters are applied, only the filtered data will be exported.
              </span>
            }
          >
            <span
              className={classNames({
                [styles.tooltipInfo]: true,
                [styles.tooltipDisabled]: !rides?.length,
              })}
            >
              <InfoIcon width={30} height={30} className={styles.infoIcon} />
            </span>
          </Tooltip>
        </div>

        {Modal}
        <div
          className={classNames({
            [styles.pagination]: true,
            [styles.gapForPagination]: rides?.length > 4,
          })}
        >
          <Pagination
            defaultCurrent={currentPage}
            total={total}
            totalPages={totalPages}
            onChange={handleChangePage}
            pageSize={limit}
          />
        </div>
      </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  role: state.authorization.role,
  rides: state.rides.rides,
  limit: state.rides.limit,
  currentPage: state.rides.currentPage,
  total: state.rides.total,
  totalPages: state.rides.totalPages,
  dataLoaded: state.rides.dataLoaded,
  loading: state.rides.loading,
  hasOwnFilter: checkForOwnFilters(state),
  myFilterParams: state.rides.myFilterParams,
  myFilterApplied: state.rides.myFilterApplied,
  uberNeedAttentionCount: state.rides.uberNeedAttentionCount,
  uberNeedAttentionRideIds: state.rides.uberNeedAttentionRideIds,
  userId: state.authorization?.user?.id,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchRides,
      setMyFilter,
      saveCustomUserFilter,
      setFilteredStatus,
      applyMyFilter,
      setShowRefreshNotification,
      generateReport,
      clearTCInfo,
      clearHOInfo,
      resetRides,
      fetchUberNeedAttentionCount,
      acknowledgeUberNeedAttention,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Rides);
